// Copyright (c) Said Borna. All rights reserved.
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type ReactElement,
    type ReactNode,
} from 'react';
import type { Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { AuthUser, UserRole } from '@/types';

/** Auth context state shape. */
interface AuthContextValue {
    /** Currently authenticated user with profile + role, or null. */
    readonly user: AuthUser | null;
    /** Raw Supabase session. */
    readonly session: Session | null;
    /** True while initial auth state is being resolved. */
    readonly isLoading: boolean;
    /** Sign in with email + password. */
    readonly signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
    /** Sign out the current user. */
    readonly signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const LOADING_TIMEOUT_MS = 10_000;
const E2E_MODE = import.meta.env.VITE_E2E_MODE === 'true';
const E2E_ROLE_KEY = 'bam:e2e:role';

interface AuthProviderProps {
    readonly children: ReactNode;
}

/**
 * Provides authentication state to the component tree.
 * Listens for Supabase auth state changes and fetches user profile + role.
 */
export function AuthProvider({ children }: AuthProviderProps): ReactElement {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<AuthUser | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    /** Fetch profile + role for a given user ID. */
    const fetchUserProfile = useCallback(async (userId: string, email: string): Promise<AuthUser | null> => {
        const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('id, email, full_name, status')
            .eq('id', userId)
            .single();

        if (profileError || !profile) {
            return null;
        }

        const { data: roleRecord, error: roleError } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', userId)
            .single();

        if (roleError || !roleRecord) {
            return null;
        }

        return {
            id: profile.id as string,
            email: (profile.email as string) ?? email,
            fullName: profile.full_name as string | null,
            role: roleRecord.role as UserRole,
            status: profile.status as AuthUser['status'],
        };
    }, []);

    useEffect(() => {
        if (!E2E_MODE) {
            return;
        }

        const syncE2EUser = (): void => {
            const role = window.localStorage.getItem(E2E_ROLE_KEY) as UserRole | null;
            if (!role) {
                setSession(null);
                setUser(null);
                setIsLoading(false);
                return;
            }

            setSession(null);
            setUser({
                id: `e2e-${role}`,
                email: `${role}@e2e.local`,
                fullName: `E2E ${role}`,
                role,
                status: 'active',
            });
            setIsLoading(false);
        };

        syncE2EUser();
        window.addEventListener('storage', syncE2EUser);
        return () => {
            window.removeEventListener('storage', syncE2EUser);
        };
    }, []);

    useEffect(() => {
        if (E2E_MODE) {
            return;
        }

        let mounted = true;

        const timeout = setTimeout(() => {
            if (mounted) {
                setIsLoading(false);
            }
        }, LOADING_TIMEOUT_MS);

        supabase.auth.getSession()
            .then(({ data: { session: initialSession } }) => {
                if (!mounted) {
                    return;
                }
                setSession(initialSession);
            })
            .catch(() => {
                if (!mounted) {
                    return;
                }
                setSession(null);
                setUser(null);
                setIsLoading(false);
            });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
            if (!mounted) {
                return;
            }

            setSession(newSession);

            if (event === 'SIGNED_OUT' || !newSession?.user) {
                setUser(null);
                setIsLoading(false);
            }
        });

        return () => {
            mounted = false;
            clearTimeout(timeout);
            subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        if (E2E_MODE) {
            return;
        }

        let mounted = true;

        if (!session?.user) {
            const frame = requestAnimationFrame(() => {
                if (mounted) {
                    setUser(null);
                    setIsLoading(false);
                }
            });
            return () => {
                mounted = false;
                cancelAnimationFrame(frame);
            };
        }

        const loadProfile = async (): Promise<void> => {
            try {
                const authUser = await fetchUserProfile(session.user.id, session.user.email ?? '');
                if (mounted) {
                    setUser(authUser);
                    setIsLoading(false);
                }
            } catch {
                if (mounted) {
                    setUser(null);
                    setIsLoading(false);
                }
            }
        };

        void loadProfile();

        return () => {
            mounted = false;
        };
    }, [fetchUserProfile, session]);

    const signIn = useCallback(async (email: string, password: string) => {
        if (E2E_MODE) {
            const inferredRole: UserRole = email.includes('admin') || email.includes('staff') ? 'staff' : 'client';
            if (password.length < 1) {
                return { error: { message: 'Password is required' } as AuthError };
            }

            window.localStorage.setItem(E2E_ROLE_KEY, inferredRole);
            setUser({
                id: `e2e-${inferredRole}`,
                email,
                fullName: `E2E ${inferredRole}`,
                role: inferredRole,
                status: 'active',
            });
            setSession(null);
            setIsLoading(false);
            return { error: null };
        }

        const { error } = await supabase.auth.signInWithPassword({ email, password });
        return { error };
    }, []);

    const signOut = useCallback(async () => {
        if (E2E_MODE) {
            window.localStorage.removeItem(E2E_ROLE_KEY);
            setUser(null);
            setSession(null);
            return;
        }

        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
    }, []);

    const value = useMemo<AuthContextValue>(
        () => ({ user, session, isLoading, signIn, signOut }),
        [user, session, isLoading, signIn, signOut],
    );

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

/**
 * Hook to access authentication state.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
